import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store } from '@ngrx/store';
import { User } from '@completion/models';
import { projectUsers } from '@completion/selectors';
import { Subject, Observable, of } from 'rxjs';
import { startWith, map, takeUntil } from 'rxjs/operators';
import { UntypedFormControl } from '@angular/forms';
import { ProjectUsers } from '@completion/actions';

export interface UserSelectDialogData {
  title?: string;
  excludeUserIds?: number[];
  preselectedUserId?: number;
  includePredicate?: (user: User) => boolean; // simplified: removed includePermissionNames
  users?: User[];
  users$?: Observable<User[]>;
}

@Component({
  selector: 'app-user-select-dialog',
  templateUrl: './user-select-dialog.component.html',
  styleUrls: ['./user-select-dialog.component.scss'],
  standalone: false,
})
export class UserSelectDialogComponent implements OnInit, OnDestroy {
  control = new UntypedFormControl('');
  users: User[] = [];
  filteredUsers$: Observable<User[]> = of([]);
  private destroy$ = new Subject<void>();
  selectedUser: User | null = null;
  title = 'Select user';

  constructor(
    private store: Store,
    private dialogRef: MatDialogRef<UserSelectDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: UserSelectDialogData,
  ) {}

  ngOnInit(): void {
    this.title = this.data?.title || this.title;

    const excludeIds = Array.isArray(this.data?.excludeUserIds) ? this.data!.excludeUserIds! : [];
    const predicate = this.data?.includePredicate;

    // Normalize exclude ids to a set of strings for robust comparison with user ids that may be string or number
    const excludeSet = new Set((excludeIds || []).map((id) => String(id)));

    const applyFilters = (list: User[]) =>
      list.filter((u) => {
        if (excludeSet.has(String((u as any).id))) return false;
        if (typeof predicate === 'function' && !predicate(u)) return false;
        return true;
      });

    if (Array.isArray(this.data?.users) && this.data!.users!.length) {
      this.users = applyFilters(this.data!.users!);
      this.initializeFiltering();
      this.applyPreselected(excludeSet);
    } else if (this.data?.users$) {
      this.data.users$!.pipe(takeUntil(this.destroy$)).subscribe((list) => {
        this.users = applyFilters(list || []);
        this.initializeFiltering();
        this.applyPreselected(excludeSet);
      });
    } else {
      // Fallback to store
      this.store.dispatch(new ProjectUsers());
      this.store
        .select(projectUsers)
        .pipe(takeUntil(this.destroy$))
        .subscribe((list) => {
          if (list) {
            this.users = applyFilters(list);
            this.initializeFiltering();
            this.applyPreselected(excludeSet);
          }
        });
    }
  }

  private initializeFiltering(): void {
    this.filteredUsers$ = this.control.valueChanges.pipe(
      startWith(''),
      map((value) => (typeof value === 'string' ? value : (value && (value as any).name) || '')),
      map((name) => this._filter(name)),
    );
  }

  private applyPreselected(excludeSet?: Set<string>): void {
    const preselected = this.data?.preselectedUserId;
    if (preselected == null) return;
    const pre = this.users.find((u) => String((u as any).id) === String(preselected));
    if (pre) {
      // Make sure preselected user wasn't excluded accidentally
      if (excludeSet && excludeSet.has(String((pre as any).id))) {
        return;
      }
      this.selectedUser = pre;
      this.control.setValue(pre);
    }
  }

  private _filter(name: string): User[] {
    const filterValue = (name || '').toLowerCase();
    return this.users.filter((u) => (u.name || '').toLowerCase().includes(filterValue));
  }

  displayUser = (user: User | string): string => {
    if (!user) return '';
    return typeof user === 'string' ? user : user.name || '';
  };

  selectUser(user: User): void {
    this.selectedUser = user;
  }

  save(): void {
    this.dialogRef.close(this.selectedUser);
  }

  cancel(): void {
    this.dialogRef.close();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
